import i18n from "i18next";
import { getRoleKey } from "src/network/User";
import type { AuthStore } from "src/stores/AuthStore";

/** Übersetzungsbereiche, aus denen die Fehlermeldungen der Endpoint-Anfragen stammen. */
type EndpointMessageNamespace = "customers_overview" | "customer_detail_page";

/**
 * Fragt einen Endpoint von Micro.Customer mit Token und Rolle des angemeldeten Benutzers ab
 * und liefert die Antwort als JSON.
 */
export const authorizedFetch = async <TResponse>(
    authStore: AuthStore,
    url: string,
    messageNamespace: EndpointMessageNamespace,
    signal?: AbortSignal
): Promise<TResponse> => {
    if (!authStore.user || !authStore.token) {
        throw new Error(String(i18n.t(`${messageNamespace}.missing_authorization`)));
    }

    let response: Response;

    try {
        response = await fetch(url, {
            method: "GET",
            headers: {
                Authorization: `${authStore.tokenType} ${authStore.token}`,
                "x-hasura-role": getRoleKey(authStore.user.role)
            },
            signal
        });
    } catch (error) {
        // Erreicht die Anfrage den Server nicht, verwirft fetch mit einem TypeError ("Failed to fetch").
        // Ein Abbruch über das Signal bleibt unverändert, damit ihn `isAbortError` erkennt.
        if (error instanceof TypeError) {
            throw new Error(String(i18n.t(`${messageNamespace}.network_error`)));
        }

        throw error;
    }

    if (!response.ok) {
        throw new Error(String(i18n.t(`${messageNamespace}.load_error`, { status: response.status })));
    }

    return (await response.json()) as TResponse;
};

export const isAbortError = (error: unknown) => error instanceof DOMException && error.name === "AbortError";

/** Übersetzt einen Fehler einer Endpoint-Anfrage in die Meldung, die der Benutzer sieht. */
export const toEndpointErrorMessage = (error: unknown, messageNamespace: EndpointMessageNamespace) =>
    error instanceof Error ? error.message : String(i18n.t(`${messageNamespace}.unknown_error`));
